import {useCallback, useEffect, useRef, useState} from "react";
import {useRealtimeChannel} from "@/api/realtime/RealtimeChannelHooks.ts";

export interface RevisionedState {
    revision: number;
}

interface RevisionedRealtimeStateOptions<T extends RevisionedState, U> {
    topic: string;
    initialValue: T;
    load: () => Promise<T>;
    update: (update: U) => Promise<T>;
}

export interface RevisionedRealtimeState<T, U> {
    value: T;
    loading: boolean;
    updating: boolean;
    error: boolean;
    update: (update: U) => Promise<boolean>;
}

export function useRevisionedRealtimeState<T extends RevisionedState, U>({
    topic,
    initialValue,
    load,
    update,
}: RevisionedRealtimeStateOptions<T, U>): RevisionedRealtimeState<T, U> {
    const {subscribe, onConnected} = useRealtimeChannel<T>(topic);
    const [value, setValue] = useState(initialValue);
    const [loading, setLoading] = useState(true);
    const [updating, setUpdating] = useState(false);
    const [error, setError] = useState(false);
    const mounted = useRef(true);
    const revision = useRef(initialValue.revision);
    const application = useRef(0);
    const mutation = useRef(0);

    const apply = useCallback((next: T) => {
        if (!mounted.current) return;
        if (next.revision < revision.current) return;
        revision.current = next.revision;
        ++application.current;
        setValue(next);
        setLoading(false);
        setError(false);
    }, []);

    useEffect(() => {
        let active = true;
        mounted.current = true;
        const refresh = async () => {
            const applicationBeforeLoad = application.current;
            try {
                const next = await load();
                if (active) apply(next);
            } catch {
                if (active && application.current === applicationBeforeLoad) {
                    setLoading(false);
                    setError(true);
                }
            }
        };
        const unsubscribeState = subscribe(next => {
            if (active) apply(next);
        });
        const unsubscribeConnection = onConnected(refresh);
        void refresh();
        return () => {
            active = false;
            mounted.current = false;
            unsubscribeState();
            unsubscribeConnection();
        };
    }, [apply, load, onConnected, subscribe]);

    const updateState = useCallback(async (change: U) => {
        const operation = ++mutation.current;
        setUpdating(true);
        setError(false);
        try {
            const next = await update(change);
            if (!mounted.current) return false;
            apply(next);
            return true;
        } catch {
            if (mounted.current && operation === mutation.current) setError(true);
            return false;
        } finally {
            if (mounted.current && operation === mutation.current) setUpdating(false);
        }
    }, [apply, update]);

    return {value, loading, updating, error, update: updateState};
}
